/* eslint-disable @typescript-eslint/no-unsafe-return,@typescript-eslint/no-explicit-any */
import PropTypes from "prop-types";
import * as React from "react";
import { AllIconNames, Button, Group, Input, InputProps } from "react-ui";

import { CurrencyInput, CurrencyInputProps } from "../CurrencyInput/CurrencyInput";
import { Override } from "../ReactUIUtilituTypes";
import { DatePasteStatus } from "../../../../MultiControls/MultiLine/TableDataPasteContext";
import { InternalIcon } from "../../../../Icons/Icon";
import { isNullOrEmpty } from "../../../../../../../Common/TypingUtils";
import { negativeValueToBrackets } from "../../../../ValueEditors/Input/InputHelpers";

function createPropsGetter<DP extends {}>(_: DP) {
    return function <P, T extends React.Component<P>>(this: T): DP & P {
        return this.props as any;
    };
}

interface FxInputState {
    focused: boolean;
}

export type FxInputProps = Override<
    CurrencyInputProps,
    {
        auto?: boolean;
        type?: "currency" | InputProps["type"];
        onRestore?: () => void;
        onValueChange: CurrencyInputProps["onValueChange"] | InputProps["onValueChange"];
        value?: React.ReactText;
        refInput?: (element: CurrencyInput | Input | null) => void;
        hideTrailingZeros?: boolean;
        onPaste?: (event: React.ClipboardEvent<unknown>) => DatePasteStatus;
        useAutoIcon?: AllIconNames;
    }
>;

export class FxInput extends React.Component<FxInputProps, FxInputState> {
    public static __KONTUR_REACT_UI__ = "FxInput";

    public static propTypes = {
        auto: PropTypes.bool,
        type: PropTypes.string,
    };

    public static defaultProps = {
        width: 250,
        type: "text",
        value: "",
    };

    public state: FxInputState = {
        focused: false,
    };

    private input: Input | CurrencyInput | null = null;

    private readonly getProps = createPropsGetter(FxInput.defaultProps);

    public blink(): void {
        this.input?.blink();
    }

    public render(): JSX.Element {
        const {
            type: _,
            onRestore: __,
            auto,
            signed,
            fractionDigits,
            integerDigits,
            useAutoIcon,
            ...rest
        } = this.props;
        const inputProps: Partial<CurrencyInputProps> = {
            align: "right",
        };

        let button = null;

        if (auto) {
            inputProps.leftIcon = <InternalIcon name={useAutoIcon ?? "MathFunctionIcon"} />;
        } else {
            button = (
                <Button
                    narrow
                    onClick={this.props.onRestore}
                    borderless={this.props.borderless}
                    disabled={this.props.disabled}>
                    {/* InternalIcon используется внутри fxInput как костыль,
                        чтобы withFocusManagement не регистрировал иконку выше input а в навигации */}
                    <InternalIcon name={"ArrowDUturnLeftDownIcon"} />
                </Button>
            );
        }

        let inputValue = this.props.value;
        if (
            !this.state.focused &&
            this.props.showNegativeAsBrackets &&
            this.props.value &&
            !isNullOrEmpty(String(this.props.value))
        ) {
            inputValue = negativeValueToBrackets(String(this.props.value));
        }

        return (
            <Group width={this.props.width}>
                {button}
                {this.getProps().type === "currency" ? (
                    <CurrencyInput
                        {...inputProps}
                        {...rest}
                        signed={signed}
                        fractionDigits={fractionDigits}
                        integerDigits={integerDigits}
                        width={"100%"}
                        ref={this.refInput}
                        value={this.props.value as CurrencyInputProps["value"]}
                        onValueChange={this.props.onValueChange as CurrencyInputProps["onValueChange"]}
                        onPaste={this.handlePaste}
                        showNegativeAsBrackets={this.props.showNegativeAsBrackets}
                    />
                ) : (
                    <Input
                        {...inputProps}
                        {...rest}
                        width={"100%"}
                        ref={this.refInput}
                        value={inputValue as InputProps["value"]}
                        onValueChange={this.props.onValueChange as InputProps["onValueChange"]}
                        onPaste={this.handlePaste}
                        onBlur={this.handleBlur}
                        onFocus={this.handleFocus}
                    />
                )}
            </Group>
        );
    }

    public readonly handlePaste = (event: React.ClipboardEvent<unknown>): DatePasteStatus =>
        (this.props.onPaste && this.props.onPaste(event)) ?? DatePasteStatus.Failure;

    private readonly handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
        if (this.props.onFocus) {
            this.props.onFocus(event);
        }

        this.setState({ focused: true });
    };

    private readonly handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
        if (this.props.onBlur) {
            this.props.onBlur(event);
        }

        this.setState({ focused: false });
    };

    public focus = () => {
        if (this.input) {
            this.input.focus();
        }
    };

    public blur = () => {
        if (this.input) {
            this.input.blur();
        }
    };

    private readonly refInput = (element: Input | CurrencyInput | null) => {
        this.input = element;

        if (this.props.refInput) {
            this.props.refInput(this.input);
        }
    };
}
